import { useAuth0 } from '@auth0/auth0-vue'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { useUserStore } from '../stores/user'
import { getDevToken, clearDevToken } from '../composables/useAuth'

/** Pages an authenticated user should never land on. */
const GUEST_PATHS = ['/', '/login']

/**
 * Validate a `returnTo` query value. Only same-origin relative paths are
 * accepted (no `//host`, no `\`, no scheme), and never a guest page — that
 * would send the guest guard into a redirect loop.
 */
export function safeReturnTo(raw: unknown): string | null {
    if (typeof raw !== 'string' || !/^\/(?![/\\])/.test(raw) || raw.includes('\\')) return null
    const path = raw.split(/[?#]/)[0] ?? ''
    return GUEST_PATHS.includes(path) ? null : raw
}

/**
 * Wait for the Auth0 SDK to initialise, then restore whatever session exists:
 * admin impersonation, Auth0 login, or the dev-login (ROPG) token.
 * Resolves `true` when a session is present.
 */
export async function resolveSession(): Promise<boolean> {
    const userStore = useUserStore()
    const { isAuthenticated, isLoading } = useAuth0()

    // Wait for Auth0 SDK to finish initializing (prevents double-redirect)
    let maxWait = 50 // 5 seconds max
    while (isLoading.value && maxWait > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
        maxWait--
    }

    // 0. If an impersonation session exists in localStorage, restore it first.
    //    On a page reload the in-memory user.value is null, so without this
    //    step the next branch (Auth0 / dev-login token) would silently fetch
    //    the admin user back and overwrite the impersonation context.
    if (await userStore.restoreImpersonationFromStorage()) return true

    // 1. Already authenticated via Auth0 SDK
    if (isAuthenticated.value) return true

    // 2. Authenticated via local store (dev-login ROPG)
    if (userStore.isAuthenticated) return true

    // 3. Try to restore from stored dev-login token (survives page reloads)
    const devToken = getDevToken()
    if (!devToken) return false

    try {
        // Fetching the existing user is more reliable than initUser on reload
        await userStore.fetchUser(devToken)
        if (userStore.isAuthenticated) return true
    } catch (fetchError) {
        // fetchUser failed, try initUser as fallback (for fresh logins)
        console.warn('Dev token fetchUser failed, trying initUser:', fetchError)
        try {
            const payload = devToken.split('.')[1]
            if (!payload) throw new Error('Invalid token format')
            const claims = JSON.parse(atob(payload))
            const email = claims.email || claims.sub
            await userStore.initUser(devToken, email, null)
            if (userStore.isAuthenticated) return true
        } catch (initError) {
            // Token expired or invalid — clear it to prevent infinite loops
            console.warn('Dev token restore failed completely:', initError)
            clearDevToken()
        }
    }
    return false
}

/**
 * Guest-only guard for `/` and `/login`. A user with a resolved session is
 * sent (replace, so Back does not return here) to a safe `returnTo` or their
 * role home. An Auth0-only session whose user is not yet in the store is left
 * to Login.vue, which also handles the needs-signup case.
 */
export async function guestOnlyGuard(to: RouteLocationNormalized): Promise<true | RouteLocationRaw> {
    await resolveSession()
    const userStore = useUserStore()
    if (!userStore.isAuthenticated) return true
    return { path: safeReturnTo(to.query.returnTo) ?? userStore.getOnboardingRoute(), replace: true }
}
